import React, { useMemo } from 'react';

const ROUND_ORDER = [
    'Pre-Season',
    ...Array.from({ length: 29 }, (_, i) => `Round ${i}`),
];

function roundSortKey(round) {
    const idx = ROUND_ORDER.indexOf(round);
    return idx >= 0 ? idx : 999;
}

function RoundBreakdown({ bets, displayMode = 'dollars', unitSize = 25, getUnitSizeForDate, advancedUnitSettings = false }) {
    const aflBets = useMemo(() => bets.filter(b => b.sport === 'afl'), [bets]);
    const isUnits = displayMode === 'units';

    const { roundData, totals } = useMemo(() => {
        const grouped = {};

        aflBets.forEach(bet => {
            const round = bet.round || 'Unknown';
            const unitDivisor = advancedUnitSettings && getUnitSizeForDate ? getUnitSizeForDate(bet.date) : unitSize;
            const displayDivisor = isUnits ? unitDivisor : 1;
            if (!grouped[round]) {
                grouped[round] = { round, wins: 0, losses: 0, cashouts: 0, pending: 0, staked: 0, returned: 0, pl: 0, plUnits: 0 };
            }
            const g = grouped[round];

            if (bet.result === 'win') g.wins++;
            else if (bet.result === 'loss') g.losses++;
            else if (bet.result === 'cash out') g.cashouts++;
            else if (bet.result === 'pending') g.pending++;

            if (!bet.bonus_bet) g.staked += bet.stake / displayDivisor;
            g.pl += bet.pl / displayDivisor;
            g.plUnits += bet.pl / unitDivisor;
            if (bet.result !== 'pending') {
                g.returned += ((bet.bonus_bet ? 0 : bet.stake) + bet.pl) / displayDivisor;
            }
        });

        const rows = Object.values(grouped).sort((a, b) => roundSortKey(a.round) - roundSortKey(b.round));

        const totals = rows.reduce(
            (acc, r) => ({
                wins: acc.wins + r.wins,
                losses: acc.losses + r.losses,
                cashouts: acc.cashouts + r.cashouts,
                pending: acc.pending + r.pending,
                staked: acc.staked + r.staked,
                returned: acc.returned + r.returned,
                pl: acc.pl + r.pl,
                plUnits: acc.plUnits + r.plUnits,
            }),
            { wins: 0, losses: 0, cashouts: 0, pending: 0, staked: 0, returned: 0, pl: 0, plUnits: 0 }
        );

        return { roundData: rows, totals };
    }, [aflBets, isUnits, unitSize, advancedUnitSettings, getUnitSizeForDate]);

    if (aflBets.length === 0) return null;

    const fmtCurrency = (v) => isUnits ? `${v.toFixed(1)}U` : `$${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const fmtPL = (v) => isUnits ? `${v >= 0 ? '+' : ''}${v.toFixed(1)}U` : `${v >= 0 ? '+' : '-'}$${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const fmtROI = (staked, pl) => staked > 0 ? `${pl >= 0 ? '+' : ''}${((pl / staked) * 100).toFixed(1)}%` : '-';
    const plColor = (v) => v > 0 ? 'text-emerald-400' : v < 0 ? 'text-red-400' : 'text-slate-400';

    const columns = [
        { key: 'round', label: 'Round', align: 'left' },
        { key: 'wins', label: 'Wins', align: 'center' },
        { key: 'losses', label: 'Losses', align: 'center' },
        { key: 'cashouts', label: 'Cashouts', align: 'center' },
        { key: 'pending', label: 'Pending', align: 'center' },
        { key: 'winRate', label: 'Win %', align: 'center' },
        { key: 'staked', label: 'Staked', align: 'left' },
        { key: 'returned', label: 'Returned', align: 'left' },
        { key: 'pl', label: 'P/L', align: 'left' },
        { key: 'plUnits', label: 'Units', align: 'left' },
        { key: 'roi', label: 'ROI', align: 'left' },
    ];

    const renderRow = (row, isTotal = false) => {
        const cellClass = `px-4 py-3 text-sm whitespace-nowrap ${isTotal ? 'font-semibold' : ''}`;

        return (
            <tr
                key={isTotal ? 'total' : row.round}
                className={isTotal ? 'bg-slate-800/50' : 'hover:bg-slate-800/30'}
            >
                <td className={`${cellClass} ${isTotal ? 'text-white font-bold' : 'text-white font-medium'}`}>
                    {isTotal ? 'Total' : row.round}
                </td>
                <td className={`${cellClass} text-center`}>
                    <span className="text-emerald-400">{row.wins}</span>
                </td>
                <td className={`${cellClass} text-center`}>
                    <span className="text-red-400">{row.losses}</span>
                </td>
                <td className={`${cellClass} text-center`}>
                    <span className="text-blue-400">{row.cashouts}</span>
                </td>
                <td className={`${cellClass} text-center`}>
                    <span className="text-amber-400">{row.pending}</span>
                </td>
                <td className={`${cellClass} text-center text-slate-300`}>
                    {(row.wins + row.losses) > 0 ? `${((row.wins / (row.wins + row.losses)) * 100).toFixed(1)}%` : '-'}
                </td>
                <td className={`${cellClass} text-slate-300`}>{fmtCurrency(row.staked)}</td>
                <td className={`${cellClass} text-slate-300`}>{fmtCurrency(row.returned)}</td>
                <td className={`${cellClass} font-semibold ${plColor(row.pl)}`}>{fmtPL(row.pl)}</td>
                <td className={`${cellClass} font-semibold ${plColor(row.plUnits)}`}>{row.plUnits >= 0 ? '+' : ''}{row.plUnits.toFixed(2)}U</td>
                <td className={`${cellClass} font-semibold ${fmtROI(row.staked, row.pl) !== '-' ? plColor(row.pl) : 'text-slate-500'}`}>
                    {fmtROI(row.staked, row.pl)}
                </td>
            </tr>
        );
    };

    return (
        <div className="bg-slate-900/50 rounded-2xl border border-slate-700/50 overflow-hidden">
            {/* Header */}
            <div className="p-4 md:p-6 pb-0 md:pb-0">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="text-base md:text-lg font-semibold text-white mb-0.5 md:mb-1">AFL Round Performance</h3>
                        <p className="text-xs md:text-sm text-slate-400 hidden sm:block">Results breakdown by round</p>
                    </div>
                    <div className={`text-lg md:text-xl font-bold ${totals.pl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {fmtPL(totals.pl)}
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-slate-800/50">
                        <tr>
                            {columns.map(col => (
                                <th
                                    key={col.key}
                                    className={`px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider ${
                                        col.align === 'center' ? 'text-center' : 'text-left'
                                    }`}
                                >
                                    {col.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700/50">
                        {roundData.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} className="px-4 py-8 text-center text-slate-500">
                                    No AFL bets recorded yet
                                </td>
                            </tr>
                        ) : (
                            <>
                                {roundData.map(row => renderRow(row))}
                                {renderRow(totals, true)}
                            </>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default RoundBreakdown;
